// Shared helpers for the day screens (C, T, W, S, R, SH).
import type { AppState, Card, LaterWhen } from '@shared/types';
import { dayKey, fmtTime, localMinutes, weekdayName, shortDate } from '@shared/rules';
import { appStore, startCard } from '../../store';
import { go } from '../../router';

// ------------------------------------------------------------------ server clock
// AppState.now carries the server's clock (debug time travel included). Track its offset from this device each time
// a new state version arrives, so timers count from Card.startedAt in the same clock.
let seenV = -1;
let offset = 0;
const track = (st: AppState | null) => { if (st && st.v !== seenV) { seenV = st.v; offset = Date.parse(st.now) - Date.now(); } };
track(appStore.get().state);
appStore.subscribe((s) => track(s.state));
export const serverNow = () => Date.now() + offset;

// ------------------------------------------------------------------ time text
export const timeOf = (iso: string | null | undefined, tz: string) => (iso ? fmtTime(localMinutes(Date.parse(iso), tz)) : '');
export const dayOf = (iso: string, tz: string) => dayKey(Date.parse(iso), tz);
/** 'TUESDAY, NOV 10' (plan card header). */
export const dateHead = (day: string) => `${weekdayName(day)}, ${shortDate(day)}`.toUpperCase();
/** 'TUE, NOV 10' (the checklist header on narrow phones, beside "1 of 4 done"). */
export const dateHeadShort = (day: string) => `${weekdayName(day).slice(0, 3)}, ${shortDate(day)}`.toUpperCase();

/** Toast after a Later chip (QS1_Stuck toastFor). */
export const laterToast = (t: LaterWhen) => (t === 'Tomorrow' ? 'Moved to tomorrow' : t === 'In 1 hour' ? 'Moved to 1 hour from now' : 'Moved to ' + t.toLowerCase());

// ------------------------------------------------------------------ cards
export function split(cards: Card[]) {
  const items = cards.filter((c) => c.st !== 'removed');
  return {
    items,
    todo: items.filter((c) => c.st === 'todo'),
    done: items.filter((c) => c.st === 'done'),
    moved: items.filter((c) => c.st === 'moved'),
  };
}

/** Start a card (optimistic) and open W. W shows `alt` instead of the title. `from` (phone v2): 'C' when started in
 *  chat, so W's ×, Done and R come back to the chat (with the 'done' note); otherwise Today. */
export function work(id: string, alt: string | null = null, from: 'C' | 'T' = 'T') {
  void startCard(id, alt);
  go('W', { cur: id, alt, from });
}

export const titleSize = (t: string, long: number, big: number, small: number) => (t.length > long ? small : big);
